import { useMemo, useState } from 'react';
import clsx from 'clsx';

interface Props {
  columns: string[];
  rows: Record<string, any>[];
  defaultSort?: { column: string; dir: 'asc' | 'desc' };
  stickyColumns?: number;
  maxHeight?: number;
}

const CURRENCY_KEYWORDS = ['balance', 'payment', 'amount', 'charge', 'adjustment', 'billed', 'collected', 'total'];
const AGE_KEYWORDS = ['days', 'age'];
const DATE_KEYWORDS = ['date'];
const PERCENT_KEYWORDS = ['%', 'rate', 'percent'];

function isCurrencyColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return CURRENCY_KEYWORDS.some(k => lower.includes(k)) && !AGE_KEYWORDS.some(k => lower.includes(k));
}
function isAgeColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return AGE_KEYWORDS.some(k => lower.includes(k));
}
function isPercentColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return PERCENT_KEYWORDS.some(k => lower.includes(k));
}
function isDateColumn(col: string): boolean {
  const lower = col.toLowerCase();
  return DATE_KEYWORDS.some(k => lower.includes(k));
}
function isNumericColumn(col: string): boolean {
  return isCurrencyColumn(col) || isAgeColumn(col) || isPercentColumn(col);
}

function fmt(col: string, value: any): string {
  if (value == null) return '';
  if (isCurrencyColumn(col) && typeof value === 'number') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  }
  if (isPercentColumn(col) && typeof value === 'number') {
    return `${value.toFixed(1)}%`;
  }
  if (isAgeColumn(col)) return String(value);
  return String(value);
}

export function DataTable({ columns, rows, defaultSort, stickyColumns = 0, maxHeight = 540 }: Props) {
  const [sort, setSort] = useState<{ column: string; dir: 'asc' | 'desc' } | null>(defaultSort ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort.column];
      const bv = b[sort.column];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
      return sort.dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return copy;
  }, [rows, sort]);

  if (!rows.length) {
    return (
      <div className="border border-border rounded-lg p-10 text-center text-text-muted text-sm">
        No rows to display.
      </div>
    );
  }

  const stickyOffsets: number[] = [];
  let acc = 0;
  for (let i = 0; i < Math.min(stickyColumns, columns.length); i++) {
    stickyOffsets.push(acc);
    acc += 180; // rough column width for sticky offset calc
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-surface">
      <div className="overflow-auto" style={{ maxHeight }}>
        <table className="min-w-full text-sm">
          <thead className="bg-page sticky top-0 z-10">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={col}
                  onClick={() => setSort(s => s?.column === col
                    ? { column: col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
                    : { column: col, dir: isNumericColumn(col) ? 'desc' : 'asc' })}
                  className={clsx(
                    'px-3 py-2 border-b border-border text-left text-[11px] uppercase tracking-wide text-text-muted font-medium select-none cursor-pointer hover:bg-accent-muted/40',
                    isNumericColumn(col) && 'text-right',
                    idx < stickyColumns && 'sticky z-20 bg-page',
                  )}
                  style={idx < stickyColumns ? { left: stickyOffsets[idx] } : undefined}
                >
                  <span>{col}</span>
                  {sort?.column === col && (
                    <span className="ml-1 text-accent">{sort.dir === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 1 ? 'bg-[#FAFAF8]' : ''}>
                {columns.map((col, ci) => (
                  <td
                    key={col}
                    className={clsx(
                      'px-3 py-2 border-b border-border-light text-text-primary whitespace-nowrap',
                      isNumericColumn(col) && 'text-right tabular-nums',
                      ci < stickyColumns && 'sticky z-10 bg-surface',
                      ci < stickyColumns && ri % 2 === 1 && 'bg-[#FAFAF8]',
                    )}
                    style={ci < stickyColumns ? { left: stickyOffsets[ci] } : undefined}
                  >
                    {fmt(col, row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 text-xs text-text-muted bg-page border-t border-border">
        {sorted.length.toLocaleString()} row{sorted.length === 1 ? '' : 's'}
      </div>
    </div>
  );
}

export { isCurrencyColumn, isAgeColumn, isPercentColumn, isDateColumn };
